<template>
  <TransitionGroup name="toast" tag="div" class="toast-container">
    <div
      v-for="toast in toasts"
      :key="toast.id"
      class="toast"
      :class="toast.type"
    >
      <div class="toast-left">
        <div class="toast-icon">
          <span v-if="toast.type === 'success'">✓</span>
          <span v-else-if="toast.type === 'error'">✕</span>
          <span v-else-if="toast.type === 'warning'">!</span>
          <span v-else>i</span>
        </div>

        <div class="toast-content">
          <div v-if="toast.title" class="toast-title">
            {{ toast.title }}
          </div>
          <div class="toast-message">
            {{ toast.message }}
          </div>
        </div>
      </div>

      <button class="toast-close" @click="removeToast(toast.id)">×</button>
    </div>
  </TransitionGroup>
</template>

<script setup>
import { ref } from "vue";

const toasts = ref([]);
let counter = 1;

function showToast({
  title = "",
  message = "",
  type = "info",
  duration = 3000,
}) {
  const id = counter++;

  const toast = {
    id,
    title,
    message,
    type,
  };

  toasts.value.push(toast);

  if (duration > 0) {
    setTimeout(() => {
      removeToast(id);
    }, duration);
  }
}

function removeToast(id) {
  toasts.value = toasts.value.filter((toast) => toast.id !== id);
}

defineExpose({
  showToast,
  removeToast,
});
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 360px;
  max-width: calc(100vw - 24px);
}

.toast {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-left: 5px solid #3b82f6;
  border-radius: 14px;
  padding: 14px 14px 14px 12px;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.12);
}

.toast-left {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
}

.toast-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
  background: #eff6ff;
  color: #2563eb;
}

.toast-title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 3px;
}

.toast-message {
  font-size: 14px;
  line-height: 1.45;
  color: #475569;
  word-break: break-word;
}

.toast-close {
  border: none;
  background: transparent;
  color: #94a3b8;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
}

.toast-close:hover {
  color: #334155;
}

.toast.success {
  border-left-color: #10b981;
}

.toast.success .toast-icon {
  background: #ecfdf5;
  color: #059669;
}

.toast.error {
  border-left-color: #ef4444;
}

.toast.error .toast-icon {
  background: #fef2f2;
  color: #dc2626;
}

.toast.warning {
  border-left-color: #f59e0b;
}

.toast.warning .toast-icon {
  background: #fffbeb;
  color: #d97706;
}

.toast.info {
  border-left-color: #3b82f6;
}

.toast.info .toast-icon {
  background: #eff6ff;
  color: #2563eb;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.25s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.toast-move {
  transition: transform 0.25s ease;
}

@media (max-width: 640px) {
  .toast-container {
    top: 12px;
    right: 12px;
    left: 12px;
    width: auto;
    max-width: none;
  }
}
</style>